import { useGetCatalogSections } from '../../../../../services/modules/catalogs/catalogs.queries';
import { EMPTY_OFFLINE_SECTION_PRODUCT_SET } from '../../../repositories/types/catalogs.repository.types';
import {
  ResolveStatusParams,
  UseCatalogCollectionSourceParams,
} from '../catalogProductsListV2.types';
import { useCatalogView } from '../contexts/CatalogView.context';
import { ProductCollectionSourceResult } from '../productCollectionSource.types';
import { useCatalogGroupParams } from './useCatalogGroupParams';
import { useCatalogListContextParams } from './useCatalogListContextParams';
import { useCatalogListErrorToast } from './useCatalogListErrorToast';
import { useCatalogProductsWindow } from './useCatalogProductsWindow';
import { useCatalogSortParams } from './useCatalogSortParams';
import { useCatalogUrlFilters } from './useCatalogUrlFilters';

const resolveStatus = ({
  isError,
  isLoading,
}: ResolveStatusParams): ProductCollectionSourceResult['status'] => {
  if (isError) {
    return 'error';
  }

  if (isLoading) {
    return 'loading';
  }

  return 'ready';
};

export const useCatalogCollectionSource = ({
  catalogId,
  anchorPage,
  pageSize,
  isOfflineMode,
  canPersist,
}: UseCatalogCollectionSourceParams): ProductCollectionSourceResult => {
  const { filters, customerIds } = useCatalogUrlFilters();
  const { gridDataInclude } = useCatalogView();

  const contextParams = useCatalogListContextParams();
  const { sortParams, isSettled: isSortSettled } = useCatalogSortParams({ catalogId, canPersist });
  const { groupParams, isSettled: isGroupSettled } = useCatalogGroupParams({
    catalogId,
    canPersist,
  });

  const sectionsQuery = useGetCatalogSections({
    id: catalogId,
    filters,
    customerIds,
    contextParams,
    groupParams,
    isOfflineMode,
  });

  const offlineSectionProductSet =
    sectionsQuery.data?.offlineSectionProductSet ?? EMPTY_OFFLINE_SECTION_PRODUCT_SET;

  const groups = sectionsQuery.data?.sections ?? [];
  const totalItems = groups.reduce((sum, group) => sum + group.itemsCount, 0);

  const pagesById = useCatalogProductsWindow({
    catalogId,
    anchorPage,
    pageSize,
    totalItems,
    contextParams,
    sortParams,
    isSortSettled,
    groupParams,
    isGroupSettled,
    filters,
    customerIds,
    gridDataInclude,
    isOfflineMode,
    offlineSectionProductSet,
  });

  useCatalogListErrorToast({ pagesById });

  return {
    status: resolveStatus({ isError: sectionsQuery.isError, isLoading: sectionsQuery.isLoading }),
    retry: sectionsQuery.refetch,
    groups,
    pagesById,
  };
};
